import { Check, ChevronRight, Star } from 'lucide-react';
import { Avatar } from '@/components/ui';
import type { DonorSummary } from '@/stores/searchStore';
import { DONATION_METHODS } from '@kulum/shared';

interface DonorCardProps {
  donor: DonorSummary;
  onOpenProfile?: (userId: string) => void;
}

const getDonationMethodLabel = (method: string): string =>
  DONATION_METHODS.find((item) => item.value === method)?.label ?? method;

const getEducationText = (donor: DonorSummary): string => {
  const name = donor.schoolName || donor.educationLevel;
  if (!name) return '学歴未登録';
  const status = donor.educationStatus || '卒業';
  return `${name} ${status}`;
};

const getIncomeText = (donor: DonorSummary): string => {
  return donor.annualIncome || '年収未登録';
};

export function DonorCard({ donor, onOpenProfile }: DonorCardProps) {
  const donationMethods = donor.donationMethods.length > 0 ? donor.donationMethods.map(getDonationMethodLabel) : [];
  const ageText = donor.age > 0 ? `${donor.age}歳` : 'ー歳';
  const heightText = typeof donor.height === 'number' ? `${donor.height}cm` : 'ーcm';
  const metaParts = [ageText, donor.residence || null, heightText].filter(Boolean);
  const hasRating = donor.reviewStats.totalCount > 0;
  const verificationBadges = [
    { key: 'identity', label: '本人', verified: donor.verifications.identity || donor.identityVerified },
    { key: 'education', label: '学歴', verified: donor.verifications.education },
    { key: 'sperm', label: '精液', verified: donor.verifications.spermTest },
    { key: 'std', label: '性病', verified: donor.verifications.stdTest },
  ];

  return (
    <button
      type="button"
      className="w-full bg-white px-4 py-4 text-left transition-colors active:bg-[#FAF8F5]"
      onClick={() => onOpenProfile?.(donor.userId)}
    >
      <div className="flex items-start gap-3.5">
        <div className="relative mt-0.5 shrink-0">
          <Avatar
            src={donor.iconUrl}
            alt={donor.displayName}
            size="sm"
            className="h-[52px] w-[52px] rounded-2xl bg-[#D4C5A9]"
          />
          {donor.identityVerified && (
            <div
              className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white bg-[#B87333]"
            >
              <Check size={9} color="white" strokeWidth={3} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[15px] font-semibold leading-tight text-[#1A1A1A]">
              {donor.displayName}
            </span>
            {hasRating && (
              <div className="flex flex-shrink-0 items-center gap-1">
                <Star size={11} color="#B87333" fill="#B87333" />
                <span className="text-[12px] font-semibold text-[#B87333]">
                  {donor.reviewStats.averageRating.toFixed(1)}
                </span>
                <span className="text-[11px] text-[#C0BDB8]">
                  ({donor.reviewStats.totalCount}件)
                </span>
              </div>
            )}
          </div>

          {metaParts.length > 0 && (
            <p className="text-[12px] text-[#888]">{metaParts.join(' · ')}</p>
          )}

          <p className="truncate text-[12px] text-[#B87333] font-medium">
            {getEducationText(donor)}
          </p>

          <p className="truncate text-[12px] text-[#888]">
            {getIncomeText(donor)}
          </p>

          {donationMethods.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {donationMethods.slice(0, 3).map((method) => (
                <span
                  key={method}
                  className="rounded-full bg-[#F0EDE8] px-2 py-0.5 text-[11px] text-[#888]"
                >
                  {method}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 pt-0.5">
            {verificationBadges.map((badge) => (
              <div key={badge.key} className="flex items-center gap-1">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: badge.verified ? '#B87333' : '#DEDBD6' }}
                />
                <span
                  className="text-[11px]"
                  style={{ color: badge.verified ? '#B87333' : '#C0BDB8', fontWeight: badge.verified ? 500 : 400 }}
                >
                  {badge.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <ChevronRight size={15} color="#C8C4BC" className="mt-1.5 shrink-0" />
      </div>
    </button>
  );
}
